import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { PsdFilterProps, FilterOption } from './types';
import FilterWrapper from '../GDS/Filter';
import { useSpecificFilter } from '../../../hooks/useFilterV2';
import {
  selectFilterValue,
  selectFilterLoading,
} from '../../../store/slices/filterSliceV2';
// @ts-ignore - useGraphql is a JS hook
import { useGraphql } from '../../hooks/useGraphql';

/**
 * PsdFilter - A filter component that handles parent-child filter relationships
 */
const PsdFilter: React.FC<PsdFilterProps> = ({
  staticOptionsFunction = () => [],
  graphqlConfig,
  filterId,
  parentFilterArray = [],
  label = '',
  enabled = true,
  selectedValue,
  menuPosition = 'bottom-left',
  showSearch = false,
  onChange,
  persistValue = false,
  selection = 'single',
  selectAllByDefault = true,
  autoSelectFirst = true,
  ...rest
}: PsdFilterProps) => {
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const isMultiple = selection === 'multiple';

  // Initialize selections for multi-select mode
  const initializeMultiSelections = (loadedOptions: FilterOption[]) => {
    if (!isMultiple || hasInitialized || loadedOptions.length === 0) {
      return false;
    }

    // Check if there's already a valid value in Redux (persistValue mode)
    if (
      persistValue &&
      Array.isArray(reduxSelectedValue) &&
      reduxSelectedValue.length > 0
    ) {
      // Validate that saved selections still exist in current options
      const validSavedValues = reduxSelectedValue.filter((savedVal: any) =>
        loadedOptions.some((option) => option.value === savedVal)
      );

      if (validSavedValues.length > 0) {
        setReduxValue(validSavedValues, null);
        setReduxEnabled(enabled);
        setHasInitialized(true);
        return true;
      }
    }

    // Use provided selectedValue prop if available
    if (
      selectedValue &&
      Array.isArray(selectedValue) &&
      selectedValue.length > 0
    ) {
      setReduxValue(selectedValue, null);
    } else if (selectAllByDefault) {
      // Select all by default
      const allValues = loadedOptions.map((opt) => opt.value);
      setReduxValue(allValues, null);
    } else {
      setReduxValue([], null);
    }

    setReduxEnabled(enabled);
    setHasInitialized(true);
    return true;
  };

  const {
    selectedValue: reduxSelectedValue,
    enabled: reduxEnabled,
    loading: reduxLoading,
    setValue: setReduxValue,
    setEnabled: setReduxEnabled,
    setLoading: setReduxLoading,
    setOptions: setReduxOptions,
  } = useSpecificFilter(filterId);

  // Get parent filter values and loading states
  const parentValues = useSelector((state) => {
    if (!parentFilterArray || parentFilterArray.length === 0) {
      return {};
    }
    const values: Record<string, any> = {};
    parentFilterArray.forEach((parentId) => {
      values[parentId] = selectFilterValue(state, parentId);
    });
    return values;
  }, shallowEqual);

  // Serialize parent values for comparison
  const parentValuesKey =
    parentFilterArray.length > 0 ? JSON.stringify(parentValues) : '';

  const parentLoadingStates = useSelector((state) => {
    if (!parentFilterArray || parentFilterArray.length === 0) {
      return [];
    }
    return parentFilterArray.map((parentId) =>
      selectFilterLoading(state, parentId)
    );
  }, shallowEqual);

  const graphqlEnabled = !!graphqlConfig;

  const {
    data: graphqlData,
    isLoading: graphqlIsLoading,
    error: graphqlError,
  } = useGraphql(
    graphqlEnabled
      ? {
          query: graphqlConfig?.query,
          variables: graphqlConfig?.variables,
          fetchPolicy: graphqlConfig?.fetchPolicy,
          enabled: graphqlConfig?.enabled ?? true,
        }
      : undefined
  );

  // Check if parents are ready: 1. Not loading, 2. Have selected values
  const areParentsReady = useMemo(() => {
    if (!parentFilterArray || parentFilterArray.length === 0) {
      return true;
    }

    // Check if any parent is loading
    const anyLoading = parentLoadingStates.some(Boolean);
    if (anyLoading) {
      return false;
    }

    // Check if all parents have selected values
    const allHaveValues = Object.values(parentValues).every(
      (value) => value !== null && value !== undefined && value !== ''
    );

    return allHaveValues;
  }, [parentFilterArray, parentLoadingStates, parentValues]);

  // Load options when parents are ready or parent values change (static mode)
  useEffect(() => {
    if (graphqlEnabled) {
      return;
    }

    if (!areParentsReady) {
      setReduxLoading(true);
      return;
    }

    const loadOptions = async () => {
      setReduxLoading(true);
      try {
        const loadedOptions = await staticOptionsFunction();
        setOptions(loadedOptions || []);
        setReduxOptions(loadedOptions || []); // Store options in Redux for child filters to access

        // Handle multi-select initialization
        if (isMultiple) {
          initializeMultiSelections(loadedOptions || []);
        } else if (loadedOptions && loadedOptions.length > 0) {
          // Single select: prioritize existing Redux value (if persistValue), then selectedValue prop, then first option (if autoSelectFirst)
          const existingValueInOptions = persistValue
            ? loadedOptions.find((opt) => opt.value === reduxSelectedValue)
            : null;

          if (existingValueInOptions) {
            setReduxValue(reduxSelectedValue, existingValueInOptions.label);
          } else if (selectedValue !== undefined && selectedValue !== null) {
            const matchingOption = loadedOptions.find(
              (opt) => opt.value === selectedValue
            );
            setReduxValue(selectedValue, matchingOption?.label || null);
          } else if (autoSelectFirst) {
            setReduxValue(loadedOptions[0].value, loadedOptions[0].label);
          } else {
            // No auto-selection - leave filter unselected
            setReduxValue(null, null);
          }
          setReduxEnabled(enabled);
        }
      } catch (error) {
        console.error(`[${filterId}] Error loading options:`, error);
        setOptions([]);
      } finally {
        setReduxLoading(false);
      }
    };

    loadOptions();
  }, [
    graphqlEnabled,
    areParentsReady,
    parentValuesKey,
    filterId,
    staticOptionsFunction,
    enabled,
    selectedValue,
    persistValue,
    setReduxValue,
    setReduxEnabled,
    setReduxLoading,
  ]);

  // Store transformResponse in a ref to avoid dependency issues
  const transformResponseRef = React.useRef(graphqlConfig?.transformResponse);
  transformResponseRef.current = graphqlConfig?.transformResponse;

  // Load options from GraphQL when graphqlConfig is provided
  useEffect(() => {
    if (!graphqlEnabled) {
      return;
    }

    if (graphqlIsLoading) {
      setReduxLoading(true);
      return;
    }

    setReduxLoading(false);

    if (graphqlError && graphqlError.length > 0) {
      console.error(
        `[${filterId}] GraphQL error loading options:`,
        graphqlError
      );
      setOptions([]);
      return;
    }

    if (!graphqlData) {
      setOptions([]);
      return;
    }

    let loadedOptions: FilterOption[] = [];

    if (typeof transformResponseRef.current === 'function') {
      try {
        loadedOptions = transformResponseRef.current(graphqlData) || [];
      } catch (error) {
        console.error(
          `[${filterId}] Error transforming GraphQL response:`,
          error
        );
        setOptions([]);
        return;
      }
    } else {
      console.error(
        `[${filterId}] graphqlConfig.transformResponse is required`
      );
      setOptions([]);
      return;
    }

    setOptions(loadedOptions);
    setReduxOptions(loadedOptions); // Store options in Redux for child filters to access

    // Handle multi-select initialization
    if (isMultiple) {
      initializeMultiSelections(loadedOptions);
    } else if (loadedOptions.length > 0) {
      // Single select: prioritize existing Redux value (if persistValue), then selectedValue prop, then first option (if autoSelectFirst)
      const existingValueInOptions = persistValue
        ? loadedOptions.find((opt) => opt.value === reduxSelectedValue)
        : null;

      if (existingValueInOptions) {
        setReduxValue(reduxSelectedValue, existingValueInOptions.label);
      } else if (selectedValue !== undefined && selectedValue !== null) {
        const matchingOption = loadedOptions.find(
          (opt) => opt.value === selectedValue
        );
        setReduxValue(selectedValue, matchingOption?.label || null);
      } else if (autoSelectFirst) {
        setReduxValue(loadedOptions[0].value, loadedOptions[0].label);
      } else {
        // No auto-selection - leave filter unselected
        setReduxValue(null, null);
      }
      setReduxEnabled(enabled);
    }
  }, [
    graphqlEnabled,
    graphqlIsLoading,
    graphqlData,
    graphqlError,
    enabled,
    selectedValue,
    persistValue,
    autoSelectFirst,
    setReduxEnabled,
    setReduxLoading,
    setReduxValue,
    filterId,
    parentValuesKey, // Re-process when parent filter values change (for dependent filters)
  ]);

  // Convert FilterOption[] to format expected by GDS Filter
  const gdsOptions = options.map((option, index) => ({
    id: `${filterId}-option-${index}`,
    label: option.label,
    value: option.value,
    disabled: option.disabled || false,
  }));

  // Find the selected FilterItem(s) based on Redux selectedValue (fully controlled by Redux)
  const selectedFilterItem = useMemo(() => {
    if (isMultiple) {
      const values = Array.isArray(reduxSelectedValue)
        ? reduxSelectedValue
        : [];
      return gdsOptions.filter((option) => values.includes(option.value));
    }
    return (
      gdsOptions.find((option) => option.value === reduxSelectedValue) || null
    );
  }, [isMultiple, reduxSelectedValue, gdsOptions]);

  // Handle value change from GDS Filter
  const handleFilterChange = (value: any) => {
    if (isMultiple) {
      // Multi-select: value is an array of FilterItem objects
      const values = Array.isArray(value) ? value : value ? [value] : [];
      const actualValues = values.map((v: any) => v?.value ?? v);

      setReduxValue(actualValues, null);

      if (onChange) {
        onChange(actualValues, filterId);
      }
    } else {
      // Single select
      if (value === undefined || value === null) {
        return;
      }
      const actualValue = value?.value || value;
      const actualLabel = value?.label || null;

      setReduxValue(actualValue, actualLabel);

      if (onChange) {
        onChange(actualValue, filterId);
      }
    }
  };

  return (
    <div className="psd-filter" data-filter-id={filterId}>
      <FilterWrapper
        label={label}
        options={gdsOptions}
        value={selectedFilterItem}
        onChange={handleFilterChange}
        disabled={!reduxEnabled}
        showSearch={showSearch}
        isClearable={false}
        selection={selection}
        controlled={true}
        skeleton={reduxLoading}
        menuPosition={menuPosition}
        {...rest}
      />
    </div>
  );
};

export default PsdFilter;
